// src/ClientDashboard.js

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { db, auth, rtDb } from './firebase';
import { signOut } from 'firebase/auth';
import * as XLSX from 'xlsx';
import { 
    collection, 
    query, 
    where, 
    onSnapshot, 
    doc,
    updateDoc,
    addDoc,
    orderBy,
    deleteDoc,
    getDoc,
    getDocs,
    setDoc
} from 'firebase/firestore';
import { CSVLink } from 'react-csv';
import { ref, onValue, set } from "firebase/database";
import { QRCodeCanvas } from 'qrcode.react';
import { useNavigate } from 'react-router-dom';
import WebhookSetupPage from './WebhookSetupPage';

// --- الثوابت ---
const statusTitles = {
  'CONFIRM': 'الأوردرات المؤكدة',
  'AI_CREATED': 'أوردرات (AI)',
  'CANCELED': 'الأوردرات الملغاة',
  'UNCLEAR': 'الأوردرات (غير واضحة)',
  'ALL': 'إجمالي الأوردرات',
};
const infoBoxH2Style = { margin: '0 0 15px 0', color: '#bd93f9', fontSize: '1.2em' };
const statCardH4Style = { margin: '0 0 10px 0', fontSize: '1.1em' };

// 🔥 الستايلات بتاعة الشات (منقولة فوق)
const chatStyles = {
  chatModalContent: {
    width: '90%',
    maxWidth: '500px',
    height: '80vh',
    padding: '0',
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'right',
  },
  chatHeader: {
    padding: '20px',
    borderBottom: '1px solid #44475a',
    background: '#44475a',
    borderRadius: '10px 10px 0 0',
    position: 'relative',
  },
  closeButton: {
    position: 'absolute',
    top: '15px',
    left: '20px',
    background: 'transparent',
    border: 'none',
    color: '#f8f8f2',
    fontSize: '28px',
    cursor: 'pointer',
    padding: '0 5px',
    lineHeight: '1',
    transition: 'color 0.2s ease',
  },
  chatWindow: {
    flexGrow: 1,
    overflowY: 'auto',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    scrollBehavior: 'smooth',
  },
  messageBubble: {
    padding: '10px 15px',
    borderRadius: '18px',
    marginBottom: '0px',
    maxWidth: '80%',
    fontSize: '15px',
    lineHeight: '1.5',
    wordBreak: 'break-word',
  },
  userMessage: {
    backgroundColor: '#005c4b',
    color: '#f8f8f2',
    alignSelf: 'flex-end',
    textAlign: 'right',
    borderRadius: '18px 18px 5px 18px',
  },
  assistantMessage: {
    backgroundColor: '#44475a',
    color: '#f8f8f2',
    alignSelf: 'flex-start',
    textAlign: 'left',
    borderRadius: '18px 18px 18px 5px',
  },
  loadingText: {
    textAlign: 'center',
    color: '#6272a4',
    fontSize: '16px',
    padding: '20px',
  },
  clickableRow: {
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
  }
};


// 🔥 ==========================================================
// 🔥 مودال عرض الشات (مع تصليح الكراش)
// 🔥 ==========================================================
function ChatHistoryModal({ order, history, loading, onClose, isManualMode, messageInput, setMessageInput, onSendMessage, isSending }) {
  const chatWindowRef = useRef(null);

  // Auto-scroll للأسفل لما الرسائل تتحمل
  useEffect(() => {
    if (chatWindowRef.current && history.length > 0) {
      chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
    }
  }, [history]);

  // دالة لتنسيق الوقت (محسّنة)
  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    
    if (isNaN(date.getTime())) {
      return ''; 
    }

    return date.toLocaleTimeString('ar-EG', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  if (!order) return null;

  return (
    <div style={styles.modalBackdrop} onClick={onClose}>
      <div style={{...styles.modalContent, ...chatStyles.chatModalContent}} onClick={(e) => e.stopPropagation()}>
        
        <div style={chatStyles.chatHeader}>
          <h3 style={{color: '#8be9fd', margin: 0}}>سجل المحادثة للعميل</h3>
          <p style={{color: '#f8f8f2', margin: '5px 0 0 0'}}>
            {order.customer_name || 'عميل'} ({order.customer_phone})
          </p>
          <button onClick={onClose} style={chatStyles.closeButton}>×</button>
        </div>
        
        <div ref={chatWindowRef} style={chatStyles.chatWindow}>
          {loading ? (
            <div style={chatStyles.loadingText}>جاري تحميل سجل المحادثة...</div>
          ) : history.length === 0 ? (
            <div style={chatStyles.loadingText}>لا يوجد سجل محادثة لهذا العميل.</div>
          ) : (
            history.map((msg, index) => (
              <div 
                key={index} 
                style={{
                  ...chatStyles.messageBubble,
                  ...(msg.role === 'user' ? chatStyles.userMessage : chatStyles.assistantMessage)
                }}
              >
                {/* 🔥 (تصليح الكراش) بنجبر المحتوى يكون نص قبل عرضه */}
                {String(msg.content).split('\n').map((line, i) => (
                  <span key={i}>{line}<br/></span>
                ))}
                
                {msg.timestamp && (
                  <div style={{
                    fontSize: '11px',
                    opacity: 0.7,
                    marginTop: '5px',
                    textAlign: msg.role === 'user' ? 'left' : 'right'
                  }}>
                    {formatMessageTime(msg.timestamp)}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* 🔥 Message Input Box - يظهر فقط في الوضع اليدوي */}
        {isManualMode && (
          <div style={{
            padding: '15px',
            borderTop: '1px solid #44475a',
            backgroundColor: '#282a36',
            display: 'flex',
            gap: '10px',
            alignItems: 'center'
          }}>
            <textarea
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  onSendMessage();
                }
              }}
              placeholder="اكتب رسالتك هنا..."
              disabled={isSending}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '8px',
                border: '1px solid #44475a',
                backgroundColor: '#44475a',
                color: '#f8f8f2',
                fontSize: '14px',
                resize: 'none',
                minHeight: '45px',
                maxHeight: '120px',
                fontFamily: 'inherit',
                direction: 'rtl',
                textAlign: 'right'
              }}
              rows="2"
            />
            <button
              onClick={onSendMessage}
              disabled={isSending || !messageInput.trim()}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: isSending || !messageInput.trim() ? '#6272a4' : '#50fa7b',
                color: '#282a36',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: isSending || !messageInput.trim() ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap'
              }}
            >
              {isSending ? '...' : '📤 إرسال'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// 🔥 ==========================================================
// 🔥 الداش بورد الحقيقية (فيها الإضافات الجديدة)
// 🔥 ==========================================================
function RealDashboard({ user, clientInfo, handleLogout }) {
  const [clientOrders, setClientOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true); 
  const [filterType, setFilterType] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [activeStatusFilter, setActiveStatusFilter] = useState('ALL');
  const [waStatus, setWaStatus] = useState('DISCONNECTED');
  const [qrString, setQrString] = useState('');
  const [showQrModal, setShowQrModal] = useState(false);
  const [isDeletingSession, setIsDeletingSession] = useState(false);
  const [searchPhoneNumber, setSearchPhoneNumber] = useState('');
  const [questions, setQuestions] = useState([]); 
  const [loadingQuestions, setLoadingQuestions] = useState(true); 
  const [selectedQuestion, setSelectedQuestion] = useState(null); 
  const [answerText, setAnswerText] = useState(''); 
  const [saveToKnowledgeBase, setSaveToKnowledgeBase] = useState(false); 
  const [isSubmitting, setIsSubmitting] = useState(false); 
  const [knowledgeBase, setKnowledgeBase] = useState([]);
  const [loadingKnowledgeBase, setLoadingKnowledgeBase] = useState(true);
  const [editingItem, setEditingItem] = useState(null); 
  const [editedAnswer, setEditedAnswer] = useState(''); 
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false); 
  const [selectedOrderForChat, setSelectedOrderForChat] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [loadingChat, setLoadingChat] = useState(false);
  
  // 🔥 --- State للإلغاء التلقائي ---
  const [autoCancelEnabled, setAutoCancelEnabled] = useState(clientInfo?.auto_cancel || false);
  const [isUpdatingAutoCancel, setIsUpdatingAutoCancel] = useState(false);

  // 🔥 --- (جديد) ---
  const [allChats, setAllChats] = useState([]);
  const [loadingChats, setLoadingChats] = useState(true);
  const [searchOrderNumber, setSearchOrderNumber] = useState('');
  const [searchChatPhone, setSearchChatPhone] = useState('');
  // 🔥 جديد: البحث برقم الأوردر في المحادثات
  const [searchLeadsOrderNumber, setSearchLeadsOrderNumber] = useState('');
  const [pendingOrders, setPendingOrders] = useState([]);
  
  // 🔥 Modal بيانات العميل
  const [showClientInfoModal, setShowClientInfoModal] = useState(false);
  const [editingWhatsapp, setEditingWhatsapp] = useState('');
  const [isSavingWhatsapp, setIsSavingWhatsapp] = useState(false);
  // 🔥 --- (انتهى الجديد) ---

  // 🔥 --- Non-AI Chat Toggle State ---
  const [nonAiChats, setNonAiChats] = useState([]);
  const [togglingChat, setTogglingChat] = useState({});

  // 🔥 --- Message Input State (for manual chat) ---
  const [messageInput, setMessageInput] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const chatListenerUnsubscribe = useRef(null);

  // 🔥 --- State للبحث في الأوردرات المعلقة ---
  const [searchPendingOrderNumber, setSearchPendingOrderNumber] = useState('');
  const [searchPendingPhone, setSearchPendingPhone] = useState('');

  // 🔥 دالة حفظ رقم الواتساب
  const handleSaveWhatsapp = async () => {
    if (!editingWhatsapp || editingWhatsapp.trim() === '') {
      alert('الرجاء إدخال رقم واتساب');
      return;
    }

    setIsSavingWhatsapp(true);
    try {
      let phoneNumber = editingWhatsapp.trim().replace(/[\s-()]/g, '');
      
      if (phoneNumber.startsWith('01') && phoneNumber.length === 11) {
        phoneNumber = '20' + phoneNumber.substring(1);
      } else if (phoneNumber.startsWith('1') && phoneNumber.length === 10) {
        phoneNumber = '20' + phoneNumber;
      } else if (phoneNumber.startsWith('+20') && phoneNumber.length === 13) {
        phoneNumber = phoneNumber.substring(1);
      } else if (phoneNumber.startsWith('0020') && phoneNumber.length === 14) {
        phoneNumber = phoneNumber.substring(2);
      }

      const clientDocRef = doc(db, 'clients_v2', clientInfo.brand_name);
      await updateDoc(clientDocRef, {
        brand_owner_whatsapp: Number(phoneNumber)
      });
      alert('✅ تم تحديث رقم الواتساب بنجاح!');
      setShowClientInfoModal(false);
    } catch (error) {
      console.error('خطأ في تحديث رقم الواتساب:', error);
      alert('❌ حدث خطأ أثناء التحديث');
    } finally {
      setIsSavingWhatsapp(false);
    }
  };

  // Effect لمزامنة إعداد الإلغاء التلقائي
  useEffect(() => {
    if (clientInfo?.auto_cancel !== undefined) {
      setAutoCancelEnabled(clientInfo.auto_cancel);
    }
  }, [clientInfo?.auto_cancel]);

  // Effect الأوردرات - 🔥 استخدام final_orders_easyorder
  useEffect(() => {
    if (!clientInfo?.brand_name) return; 
    setLoadingOrders(true);
    const ordersQuery = query(collection(db, 'final_orders_easyorder'), where('brand_name', '==', clientInfo.brand_name));
    const unsubscribe = onSnapshot(ordersQuery, (snapshot) => {
      setClientOrders(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoadingOrders(false); 
    }, (error) => { console.error("Error fetching orders:", error); setLoadingOrders(false); });
    return () => unsubscribe();
  }, [clientInfo]); 

  // Effect الواتساب
  useEffect(() => {
    if (!user?.uid) return;
    const auth_uid = user.uid;
    const statusRef = ref(rtDb, `sessions_v2/${auth_uid}/status`); 
    const qrRef = ref(rtDb, `sessions_v2/${auth_uid}/qr_string`);
    
    // 🔥 بنستخدم listener واحد للـ status والـ QR مع بعض
    const statusUnsub = onValue(statusRef, (statusSnapshot) => {
        const newStatus = statusSnapshot.val() || 'DISCONNECTED';
        console.log(`[WA Status] Changed to: ${newStatus}`);
        setWaStatus(newStatus);
        
        // 🔥 لو الحالة PENDING_SCAN، نفتح الـ modal ونستنى الـ QR
        if (newStatus === 'PENDING_SCAN') {
          setShowQrModal(true);
          console.log('[WA Status] Modal opened, waiting for QR...');
        } 
        // لو الحالة اتغيرت لحاجة تانية، نقفل الـ modal
        else if (newStatus === 'CONNECTED') {
          setShowQrModal(false);
          console.log('[WA Status] Connected! Closing modal.');
        }
    }, (error) => { 
      console.error("Error fetching WA status:", error); 
      setWaStatus('DISCONNECTED'); 
      setShowQrModal(false); 
    });

    const qrUnsub = onValue(qrRef, (qrSnapshot) => {
        const newQrString = qrSnapshot.val() || '';
        console.log(`[WA QR] ${newQrString ? 'QR received!' : 'QR cleared'}`);
        setQrString(newQrString);
    }, (error) => { 
      console.error("Error fetching WA QR string:", error); 
      setQrString(''); 
    });

    return () => { statusUnsub(); qrUnsub(); };
  }, [user?.uid]);

  // Effect الأسئلة - 🔥 استخدام brand_questions_easy_orders
  useEffect(() => {
    if (!clientInfo?.brand_name) return; 
    setLoadingQuestions(true);
    const q = query(
      collection(db, 'brand_questions_easy_orders'),
      where('brand_name', '==', clientInfo.brand_name),
      where('is_answered', '==', false), 
      orderBy('date_added', 'desc') 
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedQuestions = snapshot.docs.map(doc => ({
        id: doc.id, 
        ...doc.data()
      }));
      setQuestions(fetchedQuestions);
      setLoadingQuestions(false);
    }, (error) => {
      console.error("Error fetching questions:", error);
      setLoadingQuestions(false);
    });
    return () => unsubscribe(); 
  }, [clientInfo]); 

  // Effect قاعدة المعرفة - 🔥 استخدام knowledge_base_easy_orders
  useEffect(() => {
    if (!clientInfo?.brand_name) return;
    setLoadingKnowledgeBase(true);
    const kbQuery = query(
      collection(db, 'knowledge_base_easy_orders'),
      where('brand_name', '==', clientInfo.brand_name),
      orderBy('date_added', 'desc')
    );
    const unsubscribe = onSnapshot(kbQuery, (snapshot) => {
      const fetchedItems = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setKnowledgeBase(fetchedItems);
      setLoadingKnowledgeBase(false);
    }, (error) => {
      console.error("Error fetching knowledge base:", error);
      setLoadingKnowledgeBase(false);
    });
    return () => unsubscribe();
  }, [clientInfo]);

  // 🔥 --- Effect للـ Pending Orders - استخدام pending_orders_easyorder
  useEffect(() => {
    if (!clientInfo?.brand_name) return;
    
    const pendingQuery = query(
      collection(db, 'pending_orders_easyorder'), 
      where('brand_name', '==', clientInfo.brand_name)
    );
    
    const unsubscribe = onSnapshot(pendingQuery, (snapshot) => {
      setPendingOrders(snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })));
    }, (error) => { 
      console.error("Error fetching pending orders:", error); 
    });

    return () => unsubscribe();
  }, [clientInfo]);

  // 🔥 --- Effect لجلب non-AI chats ---
  useEffect(() => {
    if (!clientInfo?.brand_name) return;
    
    const nonAiQuery = query(
      collection(db, 'non-ai-chat'),
      where('brand', '==', clientInfo.brand_name)
    );
    
    const unsubscribe = onSnapshot(nonAiQuery, (snapshot) => {
      const chats = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setNonAiChats(chats);
    }, (error) => {
      console.error('Error fetching non-AI chats:', error);
    });
    
    return () => unsubscribe();
  }, [clientInfo]);

  // 🔥 --- Effect المحادثات - استخدام chat_histories_easy_orders
  useEffect(() => {
    if (!clientInfo?.brand_name) return;
    setLoadingChats(true);
    
    const chatQuery = query(
      collection(db, 'chat_histories_easy_orders'), 
      where('brand_name', '==', clientInfo.brand_name),
      orderBy('last_updated_timestamp', 'desc') 
    );
    
    const unsubscribe = onSnapshot(chatQuery, (snapshot) => {
      const chats = snapshot.docs.map(doc => {
        const data = doc.data();
        return { 
          id: doc.id, 
          ...data,
          customer_phone: data.customer_phone, 
          customer_name: data.messages && data.messages.length > 0 ? 'محادثة' : 'محادثة فارغة'
        }
      });
      setAllChats(chats);
      setLoadingChats(false);
    }, (error) => { 
      console.error("Error fetching all chats:", error); 
      setLoadingChats(false); 
    });

    return () => unsubscribe();
  }, [clientInfo]);

  // الدوال
  const handleReconnect = useCallback(async () => {
    if (!user?.uid || !clientInfo?.server_id) return;
    const server_id = clientInfo.server_id;
    const auth_uid = user.uid;
    
    // 🔥 لو متصل بالفعل، نسأل المستخدم لو عايز يغير الرقم
    if (waStatus === 'CONNECTED') {
      const confirmChange = window.confirm(
        '⚠️ الواتساب متصل حالياً.\n\n' +
        'هل تريد قطع الاتصال وربط رقم جديد؟\n' +
        '(سيتم حذف السيشن الحالي بالكامل)'
      );
      
      if (!confirmChange) return;
      
      // 🔥 فتح الـ modal فوراً وعرض رسالة المسح
      setShowQrModal(true);
      setQrString(''); // مسح QR القديم
      setWaStatus('INITIALIZING');
      setIsDeletingSession(true); // 🔥 تفعيل حالة المسح
      
      // إرسال أمر DELETE_CLIENT لمسح السيشن
      console.log(`🗑️ Deleting current session for ${auth_uid}...`);
      const deleteCommandRef = ref(rtDb, `commands_v2/${server_id}/${auth_uid}`);
      
      try {
        await set(deleteCommandRef, { action: 'DELETE_CLIENT' });
        console.log('✅ Delete command sent, waiting 5 seconds for server to clean up...');
        
        // 🔥 انتظار 5 ثواني عشان السيرفر يمسح السيشن بالكامل
        await new Promise(resolve => setTimeout(resolve, 5000));
        
        setIsDeletingSession(false); // 🔥 إيقاف حالة المسح
        // 🔥 نتأكد إن الحالة اتغيرت
        console.log('⏳ Waiting for status to change...');
      } catch (err) {
        console.error('❌ Error deleting session:', err);
        alert('فشل حذف السيشن. حاول مرة أخرى.');
        setShowQrModal(false);
        setWaStatus('DISCONNECTED');
        setIsDeletingSession(false);
        return;
      }
    } else {
      // لو مش متصل، نفتح الـ modal عادي
      setWaStatus('INITIALIZING');
      setQrString('');
      setShowQrModal(true);
      setIsDeletingSession(false);
    }
    
    // إرسال أمر REQUEST_QR
    const commandRef = ref(rtDb, `commands_v2/${server_id}/${auth_uid}`); 
    console.log(`🔄 Sending REQUEST_QR command for ${auth_uid} to commands_v2/${server_id}`);
    
    set(commandRef, { action: 'REQUEST_QR' })
      .then(() => { 
        console.log('✅ Command sent successfully! Waiting for server response...'); 
      })
      .catch((err) => {
          console.error('❌ Error sending command:', err);
          setWaStatus('DISCONNECTED');
          setShowQrModal(false);
          alert('فشل إرسال طلب إعادة الاتصال، حاول مرة أخرى.');
      });
  }, [user?.uid, clientInfo?.server_id, waStatus]);

  // 🔥 دالة حذف الواتساب نهائياً
  const handleDeleteWhatsApp = useCallback(async () => {
    if (!user?.uid || !clientInfo?.server_id) return;
    
    const confirmDelete = window.confirm(
      '🗑️ تحذير: حذف نهائي!\n\n' +
      'هل أنت متأكد من حذف الواتساب نهائياً؟\n' +
      'سيتم:\n' +
      '• قطع الاتصال بالواتساب\n' +
      '• حذف السيشن بالكامل من السيرفر\n' +
      '• تعيين الحالة إلى DISCONNECTED\n\n' +
      'لن تتمكن من استقبال الرسائل حتى تعيد الربط.'
    );
    
    if (!confirmDelete) return;
    
    const server_id = clientInfo.server_id;
    const auth_uid = user.uid;
    const deleteCommandRef = ref(rtDb, `commands_v2/${server_id}/${auth_uid}`);
    
    try {
      console.log(`🗑️ Permanently deleting WhatsApp session for ${auth_uid}...`);
      setWaStatus('INITIALIZING');
      
      await set(deleteCommandRef, { action: 'DELETE_CLIENT' });
      console.log('✅ Delete command sent successfully!');
      
      // انتظار 3 ثواني للسيرفر ينفذ الأمر
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      setWaStatus('DISCONNECTED');
      alert('✅ تم حذف الواتساب بنجاح!\n\nيمكنك إعادة الربط في أي وقت من زر "ربط الواتساب".');
      
    } catch (err) {
      console.error('❌ Error deleting WhatsApp:', err);
      setWaStatus('DISCONNECTED');
      alert('حدث خطأ أثناء الحذف. تم تعيين الحالة إلى غير متصل.');
    }
  }, [user?.uid, clientInfo?.server_id]);

  const handleOpenReplyModal = (question) => {
    setSelectedQuestion(question);
    setAnswerText(''); 
    setSaveToKnowledgeBase(false); 
  };

  const handleCloseReplyModal = useCallback(() => {
    if (isSubmitting) return; 
    setSelectedQuestion(null);
  }, [isSubmitting]);

  const handleSubmitAnswer = useCallback(async () => {
    if (!selectedQuestion || !answerText.trim() || isSubmitting) return;
    setIsSubmitting(true);
    const questionRef = doc(db, 'brand_questions_easy_orders', selectedQuestion.id);
    try {
      console.log(`Updating question ${selectedQuestion.id}`);
      await updateDoc(questionRef, {
        answer_text: answerText,
        is_answered: true,
        answer_date: new Date() 
      });
      if (saveToKnowledgeBase) {
        console.log(`Adding to knowledge_base_easy_orders for ${clientInfo.brand_name}`);
        await addDoc(collection(db, 'knowledge_base_easy_orders'), {
          brand_name: clientInfo.brand_name,
          question_text: selectedQuestion.whatsapp_message, 
          product_context: selectedQuestion.product_context || null,
          answer_text: answerText, 
          date_added: new Date()
        });
      }
      handleCloseReplyModal();
      alert('تم إرسال الرد بنجاح! (الرسالة ستصل للعميل خلال ثواني)');
    } catch (error) {
      console.error("Error submitting answer: ", error);
      alert('حدث خطأ أثناء إرسال الرد. حاول مرة أخرى.');
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedQuestion, answerText, saveToKnowledgeBase, clientInfo, isSubmitting, handleCloseReplyModal]); 

  const handleDeleteQuestion = useCallback(async (questionId) => {
    if (!window.confirm("هل أنت متأكد أنك تريد حذف هذا السؤال نهائياً؟ لا يمكن التراجع عن هذا الإجراء.")) {
      return;
    }
    console.log(`Attempting to delete question: ${questionId}`);
    const questionRef = doc(db, 'brand_questions_easy_orders', questionId);
    try {
      await deleteDoc(questionRef);
      console.log("Question deleted successfully!");
    } catch (error) {
      console.error("Error deleting question: ", error);
      alert('حدث خطأ أثناء حذف السؤال. حاول مرة أخرى.');
    }
  }, []); 

  const handleOpenEditModal = (item) => {
    setEditingItem(item);
    setEditedAnswer(item.answer_text);
  };

  const handleCloseEditModal = useCallback(() => {
    if (isSubmittingEdit) return;
    setEditingItem(null);
    setEditedAnswer('');
  }, [isSubmittingEdit]);

  const handleSubmitEdit = useCallback(async () => {
    if (!editingItem || !editedAnswer.trim() || isSubmittingEdit) return;
    setIsSubmittingEdit(true);
    const itemRef = doc(db, 'knowledge_base_easy_orders', editingItem.id);
    try {
      await updateDoc(itemRef, {
        answer_text: editedAnswer
      });
      handleCloseEditModal();
      alert('تم حفظ التعديل بنجاح!');
    } catch (error) {
      console.error("Error updating knowledge base item: ", error);
      alert('حدث خطأ أثناء حفظ التعديل.');
    } finally {
      setIsSubmittingEdit(false);
    }
  }, [editingItem, editedAnswer, isSubmittingEdit, handleCloseEditModal]);

  const handleDeleteKnowledgeItem = useCallback(async (itemId) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا الرد الآلي؟ سيتم حذفه نهائياً من قاعدة المعرفة.")) {
      return;
    }
    console.log(`Attempting to delete knowledge item: ${itemId}`);
    const itemRef = doc(db, 'knowledge_base_easy_orders', itemId);
    try {
      await deleteDoc(itemRef);
      console.log("Knowledge item deleted successfully!");
    } catch (error) {
      console.error("Error deleting knowledge item: ", error);
      alert('حدث خطأ أثناء حذف العنصر. حاول مرة أخرى.');
    }
  }, []);

  // 🔥 تصدير الأوردرات المعلقة إلى Excel
  const exportPendingOrdersToExcel = useCallback(() => {
    try {
      if (pendingOrders.length === 0) {
        alert('لا توجد أوردرات معلقة للتصدير');
        return;
      }

      const exportData = pendingOrders.map(order => ({
        'رقم الأوردر': order.order_id || order.order_number || 'N/A',
        'اسم العميل': order.customer_name || 'N/A',
        'رقم الهاتف': order.customer_phone || 'N/A',
        'المنتجات': order.items?.map(item => 
          `${item.title || item.product_name || item.name} (${item.quantity}×)`
        ).join(', ') || 'N/A',
        'الإجمالي': `${order.total_price || order.grand_total || 0} ${order.currency || 'EGP'}`,
        'العنوان': order.shipping_address || order.address || 'N/A',
        'المحافظة': order.province || order.governorate || 'N/A',
        'المدينة': order.city || 'N/A',
        'ملاحظات': order.notes || order.note || '-',
        'تاريخ الإنشاء': order.created_at || order.createdAt || 'N/A'
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      ws['!cols'] = [
        { wch: 15 }, { wch: 20 }, { wch: 15 }, { wch: 40 }, { wch: 15 },
        { wch: 35 }, { wch: 15 }, { wch: 15 }, { wch: 30 }, { wch: 20 }
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'الأوردرات المعلقة');

      const fileName = `أوردرات_معلقة_${clientInfo?.brand_name || 'client'}_${new Date().toLocaleDateString('ar-EG').replace(/\//g, '-')}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
      console.log('✅ تم تصدير الأوردرات المعلقة بنجاح');
    } catch (error) {
      console.error('❌ خطأ في تصدير الأوردرات:', error);
      alert('حدث خطأ أثناء تصدير البيانات');
    }
  }, [pendingOrders, clientInfo]);

  // 🔥 دالة تحديث إعداد الإلغاء التلقائي
  const handleToggleAutoCancel = useCallback(async () => {
    if (isUpdatingAutoCancel || !clientInfo?.auth_uid) return;
    
    setIsUpdatingAutoCancel(true);
    const newValue = !autoCancelEnabled;
    
    try {
      // البحث عن document العميل في clients_v2
      const clientQuery = query(
        collection(db, 'clients_v2'), 
        where('auth_uid', '==', clientInfo.auth_uid),
        where('platform_type', '==', 'easyorder')
      );
      const snapshot = await getDocs(clientQuery);
      
      if (!snapshot.empty) {
        const clientDocRef = doc(db, 'clients_v2', snapshot.docs[0].id);
        await updateDoc(clientDocRef, {
          auto_cancel: newValue
        });
        
        setAutoCancelEnabled(newValue);
        alert(newValue 
          ? '✅ تم تفعيل الإلغاء التلقائي! سيتم إلغاء الأوردرات اللي مردتش على الواتساب بعد 16 ساعة من عمل الأوردر.' 
          : '❌ تم تعطيل الإلغاء التلقائي. الأوردرات لن يتم إلغاؤها تلقائياً.'
        );
      }
    } catch (error) {
      console.error('Error updating auto_cancel:', error);
      alert('حدث خطأ أثناء تحديث الإعداد. حاول مرة أخرى.');
    } finally {
      setIsUpdatingAutoCancel(false);
    }
  }, [autoCancelEnabled, isUpdatingAutoCancel, clientInfo]);

  // 🔥 دالة فتح مودال الشات مع Real-time listener
  const handleOpenChatModal = useCallback(async (orderOrChat) => {
    if (!orderOrChat || !clientInfo?.brand_name || !orderOrChat.customer_phone) {
      console.error("Data is incomplete for chat history.", orderOrChat);
      alert("بيانات العميل غير كاملة لعرض المحادثة.");
      return;
    }

    // إلغاء أي listener سابق
    if (chatListenerUnsubscribe.current) {
      chatListenerUnsubscribe.current();
    }

    setLoadingChat(true);
    setSelectedOrderForChat(orderOrChat);
    setChatHistory([]);
    setMessageInput(''); // مسح الـ input

    const docId = `${clientInfo.brand_name}_${orderOrChat.customer_phone}`;
    const chatDocRef = doc(db, 'chat_histories_easy_orders', docId);

    try {
      // 🔥 استخدام onSnapshot للـ Real-time updates
      const unsubscribe = onSnapshot(chatDocRef, (docSnap) => {
        if (docSnap.exists()) {
          const messages = docSnap.data().messages || [];
          
          // دالة مساعدة للـ Sort عشان تتعامل مع التواريخ المختلفة
          const getSafeTime = (timestamp) => {
            if (!timestamp) return 0;
            if (timestamp.toDate) return timestamp.toDate().getTime();
            if (typeof timestamp === 'number') return timestamp;
            const date = new Date(timestamp);
            return isNaN(date.getTime()) ? 0 : date.getTime();
          };

          // ترتيب الرسائل
          messages.sort((a, b) => {
            const timeA = getSafeTime(a.timestamp);
            const timeB = getSafeTime(b.timestamp);
            return timeA - timeB;
          });

          setChatHistory(messages);
          setLoadingChat(false);
        } else {
          console.log("No chat history found for this customer.");
          setChatHistory([]);
          setLoadingChat(false);
        }
      }, (error) => {
        console.error("Error listening to chat history:", error);
        alert("حدث خطأ أثناء متابعة المحادثة.");
        setChatHistory([]);
        setLoadingChat(false);
      });

      // حفظ الـ unsubscribe function
      chatListenerUnsubscribe.current = unsubscribe;
    } catch (error) {
      console.error("Error setting up chat listener:", error);
      alert("حدث خطأ أثناء فتح المحادثة.");
      setChatHistory([]);
      setLoadingChat(false);
    }
  }, [clientInfo]);

  const handleCloseChatModal = useCallback(() => {
    // إلغاء الـ listener لو موجود
    if (chatListenerUnsubscribe.current) {
      chatListenerUnsubscribe.current();
      chatListenerUnsubscribe.current = null;
    }
    setSelectedOrderForChat(null);
    setChatHistory([]);
    setMessageInput(''); // مسح الـ input
  }, []);

  // 🔥 دالة إرسال رسالة يدوية
  const handleSendMessage = useCallback(async () => {
    if (!messageInput.trim() || isSendingMessage || !selectedOrderForChat || !clientInfo?.brand_name) {
      return;
    }

    setIsSendingMessage(true);

    try {
      const docId = `${clientInfo.brand_name}_${selectedOrderForChat.customer_phone}`;
      const chatDocRef = doc(db, 'chat_histories_easy_orders', docId);
      
      // جلب الـ document الحالي
      const docSnap = await getDoc(chatDocRef);
      
      const newMessage = {
        role: 'assistant',
        content: messageInput.trim()
      };

      const now = new Date();
      const timestamp = now.getTime();

      if (docSnap.exists()) {
        // تحديث الـ messages الموجودة
        const currentMessages = docSnap.data().messages || [];
        await updateDoc(chatDocRef, {
          messages: [...currentMessages, newMessage],
          last_updated: now,
          last_updated_timestamp: timestamp
        });
      } else {
        // إنشاء document جديد
        await setDoc(chatDocRef, {
          brand_name: clientInfo.brand_name,
          customer_phone: selectedOrderForChat.customer_phone,
          documentID: docId,
          last_updated: now,
          last_updated_timestamp: timestamp,
          messages: [newMessage]
        });
      }

      // 🔥 إرسال الرسالة على WhatsApp
      try {
        const whatsappApiUrl = `https://api-daf.dafaiagency.shop/send/${user.uid}`;
        const phoneNumber = String(selectedOrderForChat.customer_phone).replace(/\D/g, '');
        
        const response = await fetch(whatsappApiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            to: phoneNumber,
            message: messageInput.trim()
          })
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        console.log('✅ Message sent to WhatsApp successfully');
      } catch (whatsappError) {
        console.error('❌ WhatsApp send error:', whatsappError);
        alert('تم حفظ الرسالة في الشات ولكن حدث خطأ في إرسالها على WhatsApp');
      }

      setMessageInput(''); // مسح الـ input بعد الإرسال
      console.log('✅ Message sent successfully');
    } catch (error) {
      console.error('Error sending message:', error);
      alert('حدث خطأ أثناء إرسال الرسالة');
    } finally {
      setIsSendingMessage(false);
    }
  }, [messageInput, isSendingMessage, selectedOrderForChat, clientInfo, user]);

  // 🔥 دالة للتحقق إذا كان الرقم في non-AI chat
  const isNonAiChat = useCallback((phoneNumber) => {
    return nonAiChats.some(chat => chat.phoneNumber === phoneNumber);
  }, [nonAiChats]);

  // 🔥 دالة Toggle للـ non-AI chat
  const handleToggleNonAiChat = useCallback(async (phoneNumber, platform, e) => {
    e.stopPropagation(); // منع فتح المودال
    
    if (!phoneNumber || !clientInfo?.brand_name) {
      alert('بيانات غير كاملة');
      return;
    }

    setTogglingChat(prev => ({...prev, [phoneNumber]: true}));

    try {
      const docId = `${clientInfo.brand_name}_${phoneNumber}`;
      const chatDocRef = doc(db, 'non-ai-chat', docId);
      
      const isCurrentlyNonAi = isNonAiChat(phoneNumber);
      
      if (isCurrentlyNonAi) {
        // حذف من non-AI (رجوع للـ AI)
        await deleteDoc(chatDocRef);
        console.log(`✅ Removed ${phoneNumber} from non-AI chat`);
      } else {
        // إضافة لـ non-AI
        await setDoc(chatDocRef, {
          phoneNumber: phoneNumber,
          brand: clientInfo.brand_name,
          platform: platform || 'easyOrder',
          addedAt: new Date()
        });
        console.log(`✅ Added ${phoneNumber} to non-AI chat`);
      }
    } catch (error) {
      console.error('Error toggling non-AI chat:', error);
      alert('حدث خطأ أثناء تغيير الحالة');
    } finally {
      setTogglingChat(prev => ({...prev, [phoneNumber]: false}));
    }
  }, [clientInfo, isNonAiChat]);

  const handleTextareaEnterPress = useCallback((event, submitType) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault(); 
      if (submitType === 'answer') {
        if (isSubmitting || !answerText.trim()) return;
        handleSubmitAnswer();
      } 
      else if (submitType === 'edit') {
        if (isSubmittingEdit || !editedAnswer.trim()) return;
        handleSubmitEdit();
      }
    }
  }, [isSubmitting, answerText, handleSubmitAnswer, isSubmittingEdit, editedAnswer, handleSubmitEdit]); 

  // useMemos
  const stats = useMemo(() => {
    if (!clientOrders || clientOrders.length === 0) {
        return { confirmed: 0, cancelled: 0, unclear: 0, total: 0, savedShipping: 0, aiCreated: 0 };
    }
    const confirmed = clientOrders.filter((o) => o.final_status === 'CONFIRM').length;
    const aiCreated = clientOrders.filter((o) => o.final_status === 'AI_CREATED').length;
    const cancelled = clientOrders.filter((o) => o.final_status === 'CANCELED').length;
    const unclear = clientOrders.filter((o) => o.final_status === 'UNCLEAR').length;
    const savedShipping = cancelled * 60;
    return { confirmed, aiCreated, cancelled, unclear, total: clientOrders.length, savedShipping };
   }, [clientOrders]);

  // دالة للحصول على رقم الأوردر من final أو pending
  const getOrderNumberForChat = useCallback((customerPhone) => {
    if (!customerPhone) return '-';
    
    // أولاً نبحث في final_orders_easyorder
    const finalOrder = clientOrders.find(order => 
      String(order.customer_phone) === String(customerPhone)
    );
    if (finalOrder?.order_number) return finalOrder.order_number;
    
    // لو مفيش، نبحث في pending_orders_easyorder
    const pendingOrder = pendingOrders.find(order => 
      String(order.customer_phone) === String(customerPhone)
    );
    if (pendingOrder?.order_number) return `${pendingOrder.order_number} (معلق)`;
    
    return '-';
  }, [clientOrders, pendingOrders]);

  const filteredOrdersList = useMemo(() => {
    if (!clientOrders || clientOrders.length === 0) {
      return [];
    }
    // فلترة بالحالة
    const statusFiltered = clientOrders.filter(order => activeStatusFilter === 'ALL' || order.final_status === activeStatusFilter);

    // فلترة بالبحث عن رقم الأوردر
    const searchFiltered = statusFiltered.filter(order => {
      if (searchOrderNumber.trim()) {
        const orderNumberMatches = order.order_number?.toString().includes(searchOrderNumber.trim());
        if (!orderNumberMatches) return false;
      }

      // فلترة برقم التليفون
      if (searchPhoneNumber.trim()) {
        const phoneMatches = order.customer_phone?.toString().includes(searchPhoneNumber.trim());
        if (!phoneMatches) return false;
      }

      return true;
    });

    const convertTimestampToDate = (timestamp) => timestamp?.toDate ? timestamp.toDate() : null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
    const dayBefore = new Date(today); dayBefore.setDate(today.getDate() - 2);

    const filteredByDate = searchFiltered.filter(order => {
      const orderDate = convertTimestampToDate(order.confirmation_date);
      if (!orderDate) return false;
      const orderDateNormalized = new Date(orderDate); orderDateNormalized.setHours(0, 0, 0, 0);
      switch (filterType) {
        case 'today': return orderDateNormalized.getTime() === today.getTime();
        case 'yesterday': return orderDateNormalized.getTime() === yesterday.getTime();
        case 'dayBefore': return orderDateNormalized.getTime() === dayBefore.getTime();
        case 'custom':
          if (!startDate || !endDate) return true;
          const start = new Date(startDate); start.setHours(0, 0, 0, 0);
          const end = new Date(endDate); end.setHours(23, 59, 59, 999);
          return orderDate >= start && orderDate <= end;
        case 'all': default: return true;
      }
    });

    // ترتيب تنازلي حسب وقت الإنشاء (الأحدث فوق)
    const sortedByDateDesc = filteredByDate.sort((a, b) => {
      const dateA = convertTimestampToDate(a.confirmation_date)?.getTime() || 0;
      const dateB = convertTimestampToDate(b.confirmation_date)?.getTime() || 0;
      return dateB - dateA;
    });

    return sortedByDateDesc.map(order => {
      const dateObj = convertTimestampToDate(order.confirmation_date);
      const formattedDate = dateObj ? dateObj.toLocaleString('ar-EG', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : 'Invalid Date';
      return { ...order, formattedCreatedAt: formattedDate };
    });
     }, [clientOrders, activeStatusFilter, filterType, startDate, endDate, searchOrderNumber, searchPhoneNumber]);

  // 🔥 useMemo للأوردرات المعلقة مع الفلترة
  const filteredPendingOrders = useMemo(() => {
    if (!pendingOrders || pendingOrders.length === 0) return [];

    // فلترة الأوردرات
    let filtered = pendingOrders.filter(order => {
      // فلترة برقم الأوردر
      if (searchPendingOrderNumber.trim()) {
        const orderNumberMatches = order.order_number?.toString().includes(searchPendingOrderNumber.trim());
        if (!orderNumberMatches) return false;
      }
      // فلترة برقم التليفون
      if (searchPendingPhone.trim()) {
        const phoneMatches = order.customer_phone?.toString().includes(searchPendingPhone.trim());
        if (!phoneMatches) return false;
      }
      return true;
    });

    // ترتيب حسب وقت الإنشاء (الأحدث أولاً)
    filtered.sort((a, b) => {
      const dateA = (a.confirmation_date || a.created_at)?.toDate?.() || new Date(a.confirmation_date || a.created_at || 0);
      const dateB = (b.confirmation_date || b.created_at)?.toDate?.() || new Date(b.confirmation_date || b.created_at || 0);
      return dateB - dateA;
    });
    return filtered;
  }, [pendingOrders, searchPendingOrderNumber, searchPendingPhone]);

  const csvHeaders = [
    { label: "Order ID", key: "order_id" },
    { label: "رقم الأوردر", key: "order_number" },
    { label: "وقت الإنشاء", key: "formattedCreatedAt" },
    { label: "اسم العميل", key: "customer_name" },
    { label: "تليفون العميل", key: "customer_phone" },
    { label: "السعر الإجمالي", key: "total_price" },
    { label: "المنتجات", key: "product_names" },
    { label: "الرسالة", key: "message" },
    { label: "الحالة النهائية", key: "final_status" }
   ];

  const formatFirestoreTimestamp = (timestamp) => {
    if (!timestamp) return 'غير متوفر';
    
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } 
    else if (typeof timestamp === 'number') {
      date = new Date(timestamp);
    }
    else {
      date = new Date(timestamp);
    }
    
    if (isNaN(date.getTime())) return 'وقت غير صحيح';

    return date.toLocaleString('ar-EG', {
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit', 
      minute: '2-digit'
    });
  };

  // JSX
  // تحديد اللون والنص بناءً على حالة النظام
  const systemStatusColor = clientInfo.isActive ? '#50fa7b' : '#ff5555';
  const systemStatusTitle = clientInfo.isActive ? 'النظام مُفعّل' : 'النظام مُعطّل';

  return (
    <div style={styles.dashboardContainer}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ 
            color: '#FAFAFA', 
            margin: 0, 
            display: 'flex', 
            alignItems: 'center', 
            gap: '10px' 
        }}>
            أهلاً بك، 
            <button
              onClick={() => {
                setEditingWhatsapp(clientInfo.brand_owner_whatsapp || '');
                setShowClientInfoModal(true);
              }}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#bd93f9',
                cursor: 'pointer',
                fontSize: 'inherit',
                fontWeight: 'bold',
                textDecoration: 'underline',
                padding: 0
              }}
              title="اضغط لعرض البيانات"
            >
              {clientInfo.brand_name}
            </button>
            <span 
                style={{
                    width: '18px', 
                    height: '18px', 
                    borderRadius: '50%', 
                    backgroundColor: systemStatusColor,
                    border: '1px solid rgba(255,255,255,0.2)',
                    flexShrink: 0 
                }}
                title={systemStatusTitle}
            ></span>
        </h1>
        <button onClick={handleLogout} style={styles.logoutButton}>تسجيل خروج</button>
      </div>

      <div style={styles.topInfoContainer}>
        <div style={{ ...styles.infoBox, flexGrow: 2, background: 'rgba(80, 250, 123, 0.1)', borderColor: '#50fa7b' }}>
          <h2 style={infoBoxH2Style}>رصيدك الحالي</h2>
          <h3 style={{ fontSize: '32px', color: '#50fa7b', margin: 0 }}>{clientInfo.balance || 0} جنيه</h3>
        </div>
        <div style={{ ...styles.infoBox, ...styles.savedBox }}>
          <h2 style={infoBoxH2Style}>تكلفة الشحن اللي وفرتهالك 💰</h2>
          <h3 style={{ fontSize: '28px', color: '#ffb8b8', margin: 0 }}>{stats?.savedShipping ?? 0} جنيه</h3>
        </div>
        <div style={{ ...styles.infoBox }}>
          <h2 style={infoBoxH2Style}>حالة الواتساب</h2>
          <div style={styles.waStatusInline}>
            <span style={{...styles.statusLight, backgroundColor: waStatus === 'CONNECTED' ? '#50fa7b' : waStatus === 'PENDING_SCAN' || waStatus === 'INITIALIZING' ? '#f1fa8c' : '#ff5555' }}></span>
            <span style={{ fontSize: '16px' }}>
              {waStatus === 'CONNECTED' && 'متصل'}
              {waStatus === 'PENDING_SCAN' && 'في انتظار المسح...'}
              {waStatus === 'DISCONNECTED' && 'غير متصل'}
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '15px' }}>
            <button onClick={handleReconnect} style={styles.reconnectButton}>
              {waStatus === 'CONNECTED' ? '🔄 إعادة ربط / تغيير الرقم' : '📱 ربط الواتساب'}
            </button>
            {waStatus === 'CONNECTED' && (
              <button onClick={handleDeleteWhatsApp} style={styles.deleteWhatsAppButton}>
                🗑️ حذف الواتساب نهائياً
              </button>
            )}
          </div>
        </div>
      </div>

      <div style={styles.cardContainer}>
          <div style={{ ...styles.statCard, ...(activeStatusFilter === 'CONFIRM' && styles.activeCardHighlight) }} onClick={() => setActiveStatusFilter('CONFIRM')}>
            <h4 style={statCardH4Style}>أوردرات مؤكدة ✅</h4>
            <p style={styles.statNumber}>{stats?.confirmed ?? 0}</p>
            <p style={styles.statPercentage}>{stats?.total > 0 ? ((stats.confirmed / stats.total) * 100).toFixed(1) : 0}%</p>
          </div>
          <div style={{ ...styles.statCard, ...(activeStatusFilter === 'AI_CREATED' && styles.activeCardHighlight) }} onClick={() => setActiveStatusFilter('AI_CREATED')}>
            <h4 style={statCardH4Style}>أوردرات (AI) 🤖</h4>
            <p style={styles.statNumber}>{stats?.aiCreated ?? 0}</p>
            <p style={styles.statPercentage}>{stats?.total > 0 ? ((stats.aiCreated / stats.total) * 100).toFixed(1) : 0}%</p>
          </div>
          <div style={{ ...styles.statCard, ...(activeStatusFilter === 'CANCELED' && styles.activeCardHighlight) }} onClick={() => setActiveStatusFilter('CANCELED')}>
            <h4 style={statCardH4Style}>أوردرات ملغاة ❌</h4>
            <p style={styles.statNumber}>{stats?.cancelled ?? 0}</p>
            <p style={styles.statPercentage}>{stats?.total > 0 ? ((stats.cancelled / stats.total) * 100).toFixed(1) : 0}%</p>
          </div>
          <div 
            style={{ 
              ...styles.statCard, 
              ...(activeStatusFilter === 'UNCLEAR' && styles.activeCardHighlight),
              cursor: 'default'
            }} 
          >
            <div onClick={() => setActiveStatusFilter('UNCLEAR')} style={{ cursor: 'pointer' }}>
              <h4 style={statCardH4Style}>أوردرات (غير واضحة) ❓</h4>
              <p style={styles.statNumber}>{stats?.unclear ?? 0}</p>
              <p style={styles.statPercentage}>{stats?.total > 0 ? ((stats.unclear / stats.total) * 100).toFixed(1) : 0}%</p>
            </div>
            <div style={styles.autoCancelToggleContainer}>
              <button
                onClick={handleToggleAutoCancel}
                disabled={isUpdatingAutoCancel}
                style={{
                  ...styles.autoCancelToggle,
                  backgroundColor: autoCancelEnabled ? '#50fa7b' : '#6272a4',
                  opacity: isUpdatingAutoCancel ? 0.6 : 1
                }}
              >
                {isUpdatingAutoCancel ? '...' : (
                  autoCancelEnabled 
                    ? '✓ إلغاء تلقائي (16 ساعة)' 
                    : '✕ إلغاء تلقائي معطل'
                )}
              </button>
            </div>
          </div>
          <div style={{ ...styles.statCard, ...(activeStatusFilter === 'ALL' && styles.activeCardHighlight) }} onClick={() => setActiveStatusFilter('ALL')}>
            <h4 style={statCardH4Style}>إجمالي الأوردرات 📦</h4>
            <p style={styles.statNumber}>{stats?.total ?? 0}</p>
            <p style={styles.statPercentage}>100%</p>
          </div>
      </div>

      {/* --- جدول الأوردرات --- */}
      <div style={styles.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h2 style={{ color: '#f8f8f2', margin: 0 }}>{statusTitles[activeStatusFilter]} (العدد: {filteredOrdersList.length})</h2>
          <CSVLink data={filteredOrdersList} headers={csvHeaders} filename={`${clientInfo.brand_name}-${activeStatusFilter.toLowerCase()}-orders.csv`} style={styles.downloadButton}>
            تحميل كـ CSV
          </CSVLink>
        </div>
        
        {/* حقول البحث */}
        <div style={{ marginBottom: '15px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="🔍 ابحث برقم الأوردر..."
            value={searchOrderNumber}
            onChange={(e) => setSearchOrderNumber(e.target.value)}
            style={{
              ...styles.filterInput,
              flex: '1',
              minWidth: '250px',
              fontSize: '16px',
              padding: '12px 15px'
            }}
          />
          <input
            type="text"
            placeholder="📱 ابحث برقم التليفون..."
            value={searchPhoneNumber}
            onChange={(e) => setSearchPhoneNumber(e.target.value)}
            style={{
              ...styles.filterInput,
              flex: '1',
              minWidth: '250px',
              fontSize: '16px',
              padding: '12px 15px'
            }}
          />
          {(searchOrderNumber || searchPhoneNumber) && (
            <button
              onClick={() => {
                setSearchOrderNumber('');
                setSearchPhoneNumber('');
              }}
              style={{
                ...styles.filterButton,
                background: '#ff5555',
                color: '#f8f8f2',
                border: 'none',
                padding: '12px 20px'
              }}
            >
              مسح البحث ✕
            </button>
          )}
        </div>
        
        <div style={styles.filterContainer}>
           <button style={{...styles.filterButton, ...(filterType === 'all' ? styles.activeFilterButton : {}) }} onClick={() => setFilterType('all')}>All Orders</button>
           <button style={{...styles.filterButton, ...(filterType === 'today' ? styles.activeFilterButton : {}) }} onClick={() => setFilterType('today')}>Today</button>
           <button style={{...styles.filterButton, ...(filterType === 'yesterday' ? styles.activeFilterButton : {}) }} onClick={() => setFilterType('yesterday')}>Yesterday</button>
           <button style={{...styles.filterButton, ...(filterType === 'dayBefore' ? styles.activeFilterButton : {}) }} onClick={() => setFilterType('dayBefore')}>Day Before</button>
            <span style={{ marginLeft: '10px', marginRight: '5px', color: '#ccc' }}>From:</span>
           <input type="date" style={styles.filterInput} value={startDate} onChange={(e) => { setStartDate(e.target.value); setFilterType('custom'); }} />
           <span style={{ marginLeft: '10px', marginRight: '5px', color: '#ccc' }}>To:</span>
           <input type="date" style={styles.filterInput} value={endDate} onChange={(e) => { setEndDate(e.target.value); setFilterType('custom'); }} />
        </div>
        <div style={{ maxHeight: '450px', overflowY: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Order ID</th>
                <th style={styles.th}>رقم الأوردر</th>
                <th style={styles.th}>وقت الإنشاء</th>
                <th style={styles.th}>اسم العميل</th>
                <th style={styles.th}>التليفون</th>
                <th style={styles.th}>السعر</th>
                <th style={styles.th}>المنتجات</th>
                <th style={styles.th}>الرسالة</th>
                <th style={styles.th}>وضع الشات</th>
              </tr>
            </thead>
            <tbody>
              {loadingOrders ? (
                <tr><td colSpan="9" style={{ ...styles.td, textAlign: 'center', color: '#6272a4' }}>جاري تحميل الأوردرات...</td></tr>
              ) : filteredOrdersList.length > 0 ? (
                filteredOrdersList.map((order, index) => ( 
                  <tr 
                    key={order.id} 
                    style={{
                      ...(index % 2 === 0 ? styles.trEven : styles.trOdd),
                      ...chatStyles.clickableRow
                    }}
                    onClick={() => handleOpenChatModal(order)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(139, 233, 253, 0.08)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'transparent' : 'rgba(255, 255, 255, 0.02)';
                    }}
                  >
                    <td style={styles.td}>{order.order_id}</td>
                    <td style={styles.td}>{order.order_number}</td>
                    <td style={styles.td}>{order.formattedCreatedAt}</td>
                    <td style={styles.td}>{order.customer_name}</td>
                    <td style={styles.td}>{order.customer_phone}</td>
                    <td style={styles.td}>{order.total_price}</td>
                    <td style={styles.td}>{order.product_names}</td>
                    <td style={styles.td}>{order.message}</td>
                    <td style={styles.td}>
                      <button
                        onClick={(e) => handleToggleNonAiChat(order.customer_phone, 'easyOrder', e)}
                        disabled={togglingChat[order.customer_phone]}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '5px',
                          border: 'none',
                          cursor: togglingChat[order.customer_phone] ? 'wait' : 'pointer',
                          fontWeight: 'bold',
                          fontSize: '12px',
                          transition: 'all 0.2s',
                          backgroundColor: isNonAiChat(order.customer_phone) ? '#ff5555' : '#50fa7b',
                          color: '#282a36'
                        }}
                      >
                        {togglingChat[order.customer_phone] 
                          ? '...' 
                          : isNonAiChat(order.customer_phone) 
                            ? '🤖 AI' 
                            : '👤 Manual'
                        }
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" style={{ ...styles.td, textAlign: 'center', color: '#6272a4' }}>
                    لا يوجد أوردرات لعرضها (حسب الفلتر الحالي).
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- جدول الأوردرات المعلقة --- */}
      <div style={styles.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h2 style={{ color: '#f8f8f2', margin: 0 }}>
            ⏳ الأوردرات المعلقة (Pending Orders)
            <span style={{...styles.questionCountBadge, backgroundColor: '#f1fa8c', color: '#282a36'}}>
              {filteredPendingOrders.length}
            </span>
          </h2>
          <button 
            onClick={exportPendingOrdersToExcel}
            style={{
              background: 'linear-gradient(135deg, #50fa7b 0%, #8be9fd 100%)',
              color: '#282a36',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '14px',
              transition: 'all 0.3s ease',
              boxShadow: '0 2px 8px rgba(80, 250, 123, 0.3)',
            }}
            title="تصدير إلى Excel"
          >
            📥 تصدير Excel
          </button>
        </div>
        <p style={{color: '#6272a4', margin: '-10px 0 20px 0', fontSize: '14px'}}>
          هذه أوردرات لم يتم تأكيدها أو إلغاؤها بعد - تحتاج متابعة.
        </p>
        
        {/* حقول البحث */}
        <div style={{ marginBottom: '15px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="🔍 ابحث برقم الأوردر..."
            value={searchPendingOrderNumber}
            onChange={(e) => setSearchPendingOrderNumber(e.target.value)}
            style={{
              ...styles.filterInput,
              flex: '1',
              minWidth: '250px',
              fontSize: '16px',
              padding: '12px 15px'
            }}
          />
          <input
            type="text"
            placeholder="📱 ابحث برقم التليفون..."
            value={searchPendingPhone}
            onChange={(e) => setSearchPendingPhone(e.target.value)}
            style={{
              ...styles.filterInput,
              flex: '1',
              minWidth: '250px',
              fontSize: '16px',
              padding: '12px 15px'
            }}
          />
          {(searchPendingOrderNumber || searchPendingPhone) && (
            <button
              onClick={() => {
                setSearchPendingOrderNumber('');
                setSearchPendingPhone('');
              }}
              style={{
                ...styles.filterButton,
                background: '#ff5555',
                color: '#f8f8f2',
                border: 'none',
                padding: '12px 20px'
              }}
            >
              مسح البحث ✕
            </button>
          )}
        </div>
        
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>رقم الأوردر</th>
                <th style={styles.th}>وقت الإنشاء</th>
                <th style={styles.th}>اسم العميل</th>
                <th style={styles.th}>التليفون</th>
                <th style={styles.th}>السعر</th>
                <th style={styles.th}>المنتجات</th>
                <th style={styles.th}>الرسالة</th>
                <th style={styles.th}>وضع الشات</th>
              </tr>
            </thead>
            <tbody>
              {filteredPendingOrders.length > 0 ? (
                filteredPendingOrders.map((order, index) => {
                  const createdAt = order.confirmation_date || order.created_at;
                  const formattedDate = createdAt ? formatFirestoreTimestamp(createdAt) : 'N/A';
                  
                  return (
                    <tr 
                      key={order.id} 
                      style={{
                        ...(index % 2 === 0 ? styles.trEven : styles.trOdd),
                        ...chatStyles.clickableRow
                      }}
                      onClick={() => handleOpenChatModal(order)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(241, 250, 140, 0.08)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'transparent' : 'rgba(255, 255, 255, 0.02)';
                      }}
                    >
                      <td style={{...styles.td, color: '#f1fa8c', fontWeight: 'bold'}}>{order.order_number || 'N/A'}</td>
                      <td style={styles.td}>{formattedDate}</td>
                      <td style={styles.td}>{order.customer_name || 'N/A'}</td>
                      <td style={styles.td}>{order.customer_phone || 'N/A'}</td>
                      <td style={styles.td}>{order.total_price || 'N/A'}</td>
                      <td style={styles.td}>{order.product_names || order.products?.map(p => p.name).join(', ') || 'N/A'}</td>
                      <td style={styles.td}>{order.message || order.whatsapp_message || '-'}</td>
                      <td style={styles.td}>
                        <button
                          onClick={(e) => handleToggleNonAiChat(order.customer_phone, 'easyOrder', e)}
                          disabled={togglingChat[order.customer_phone]}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '5px',
                            border: 'none',
                            cursor: togglingChat[order.customer_phone] ? 'wait' : 'pointer',
                            fontWeight: 'bold',
                            fontSize: '12px',
                            transition: 'all 0.2s',
                            backgroundColor: isNonAiChat(order.customer_phone) ? '#ff5555' : '#50fa7b',
                            color: '#282a36'
                          }}
                        >
                          {togglingChat[order.customer_phone] 
                            ? '...' 
                            : isNonAiChat(order.customer_phone) 
                              ? '🤖 AI' 
                              : '👤 Manual'
                          }
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="8" style={{ ...styles.td, textAlign: 'center', color: '#6272a4' }}>
                    {(searchPendingOrderNumber || searchPendingPhone) 
                      ? 'لا توجد أوردرات معلقة تطابق البحث'
                      : 'لا توجد أوردرات معلقة - كل شيء تمام! ✅'
                    }
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* 🔥 --- جدول العملاء في الوضع اليدوي --- */}
      <div style={styles.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h2 style={{ color: '#f8f8f2', margin: 0 }}>
            👤 المحادثات اليدوية
            <span style={{...styles.questionCountBadge, backgroundColor: '#ff5555', color: '#f8f8f2'}}>
              {nonAiChats.length}
            </span>
          </h2>
        </div>
        <p style={{color: '#6272a4', margin: '-10px 0 20px 0', fontSize: '14px'}}>
          العملاء اللي قررت تتابعهم يدوياً (مش الـ AI اللي بيرد عليهم)
        </p>
        
        {nonAiChats.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6272a4' }}>
            <p style={{ fontSize: '18px', margin: 0 }}>
              🤖 كل المحادثات بيتابعها الـ AI حالياً
            </p>
            <p style={{ fontSize: '14px', marginTop: '10px' }}>
              لو عايز تتابع عميل يدوياً، اضغط على زرار "وضع الشات" في أي جدول
            </p>
          </div>
        ) : (
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>رقم العميل</th>
                  <th style={styles.th}>Platform</th>
                  <th style={styles.th}>تاريخ الإضافة</th>
                  <th style={styles.th}>الإجراءات</th>
                </tr>
              </thead>
              <tbody>
                {nonAiChats.map((chat, index) => (
                  <tr 
                    key={chat.phoneNumber || index}
                    style={{
                      ...(index % 2 === 0 ? styles.trEven : styles.trOdd),
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      // فتح الشات عند الضغط على الصف
                      const chatData = {
                        customer_phone: chat.phoneNumber,
                        customer_name: 'عميل'
                      };
                      handleOpenChatModal(chatData);
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(139, 233, 253, 0.08)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#282a36' : '#1e1f29';
                    }}
                  >
                    <td style={styles.td}>{chat.phoneNumber || 'N/A'}</td>
                    <td style={styles.td}>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        backgroundColor: chat.platform === 'easyOrder' ? '#8be9fd' : '#50fa7b',
                        color: '#282a36'
                      }}>
                        {chat.platform === 'easyOrder' ? '📦 EasyOrder' : '🛒 Shopify'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {chat.addedAt ? new Date(chat.addedAt.seconds * 1000).toLocaleString('ar-EG', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : '-'}
                    </td>
                    <td style={styles.td}>
                      <button
                        onClick={(e) => handleToggleNonAiChat(chat.phoneNumber, chat.platform, e)}
                        disabled={togglingChat[chat.phoneNumber]}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '5px',
                          border: 'none',
                          cursor: togglingChat[chat.phoneNumber] ? 'wait' : 'pointer',
                          fontWeight: 'bold',
                          fontSize: '12px',
                          transition: 'all 0.2s',
                          backgroundColor: '#50fa7b',
                          color: '#282a36'
                        }}
                      >
                        {togglingChat[chat.phoneNumber] ? '...' : '🤖 تفعيل AI'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 🔥 --- جدول المحادثات --- */}
      <div style={styles.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h2 style={{ color: '#f8f8f2', margin: 0 }}>
            أحدث المحادثات (Leads)
            <span style={{...styles.questionCountBadge, backgroundColor: '#8be9fd', color: '#282a36'}}>
              {(() => {
                try {
                  if (!searchChatPhone.trim()) return allChats.length;
                  return allChats.filter(chat => {
                    const phoneStr = String(chat.customer_phone || '');
                    return phoneStr.includes(searchChatPhone.trim());
                  }).length;
                } catch (error) {
                  return 0;
                }
              })()}
            </span>
          </h2>
        </div>
        <p style={{color: '#6272a4', margin: '-10px 0 20px 0', fontSize: '14px'}}>
          هنا تظهر كل المحادثات (حتى اللي معملتش أوردر) - اضغط على أي صف لعرض الشات.
        </p>
        
        {/* حقول البحث في المحادثات: رقم العميل + رقم الأوردر */}
        <div style={{ marginBottom: '15px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="🔍 ابحث برقم العميل..."
            value={searchChatPhone}
            onChange={(e) => setSearchChatPhone(e.target.value)}
            style={{
              ...styles.filterInput,
              flex: '1',
              minWidth: '220px',
              fontSize: '16px',
              padding: '12px 15px'
            }}
          />
          <input
            type="text"
            placeholder="🔍 ابحث برقم الأوردر..."
            value={searchLeadsOrderNumber}
            onChange={(e) => setSearchLeadsOrderNumber(e.target.value)}
            style={{
              ...styles.filterInput,
              flex: '1',
              minWidth: '220px',
              fontSize: '16px',
              padding: '12px 15px'
            }}
          />
          {(searchChatPhone || searchLeadsOrderNumber) && (
            <button
              onClick={() => {
                setSearchChatPhone('');
                setSearchLeadsOrderNumber('');
              }}
              style={{
                ...styles.filterButton,
                background: '#ff5555',
                color: '#f8f8f2',
                border: 'none',
                padding: '12px 20px'
              }}
            >
              مسح البحث ✕
            </button>
          )}
        </div>
        
        <div style={{ maxHeight: '450px', overflowY: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{...styles.th, width: '25%'}}>الحالة</th>
                <th style={styles.th}>رقم العميل</th>
                <th style={styles.th}>رقم الأوردر</th>
                <th style={{...styles.th, width: '30%'}}>آخر رسالة</th>
                <th style={styles.th}>آخر تحديث</th>
                <th style={styles.th}>وضع الشات</th>
              </tr>
            </thead>
            <tbody>
              {loadingChats ? (
                <tr><td colSpan="6" style={{ ...styles.td, textAlign: 'center', color: '#6272a4' }}>جاري تحميل المحادثات...</td></tr>
              ) : allChats.filter(chat => {
                // فلترة برقم العميل
                const phoneMatch = searchChatPhone.trim() ? String(chat.customer_phone || '').includes(searchChatPhone.trim()) : true;
                // فلترة برقم الأوردر
                let orderMatch = true;
                if (searchLeadsOrderNumber.trim()) {
                  const orderNum = getOrderNumberForChat(chat.customer_phone);
                  orderMatch = orderNum && orderNum.toString().includes(searchLeadsOrderNumber.trim());
                }
                return phoneMatch && orderMatch;
              }).length > 0 ? (
                allChats
                  .filter(chat => {
                    // فلترة برقم العميل
                    const phoneMatch = searchChatPhone.trim() ? String(chat.customer_phone || '').includes(searchChatPhone.trim()) : true;
                    // فلترة برقم الأوردر
                    let orderMatch = true;
                    if (searchLeadsOrderNumber.trim()) {
                      const orderNum = getOrderNumberForChat(chat.customer_phone);
                      orderMatch = orderNum && orderNum.toString().includes(searchLeadsOrderNumber.trim());
                    }
                    return phoneMatch && orderMatch;
                  })
                  .map((chatDoc, index) => ( 
                  <tr 
                    key={chatDoc.id} 
                    style={{
                      ...(index % 2 === 0 ? styles.trEven : styles.trOdd),
                      ...chatStyles.clickableRow
                    }}
                    onClick={() => handleOpenChatModal(chatDoc)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(139, 233, 253, 0.08)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'transparent' : 'rgba(255, 255, 255, 0.02)';
                    }}
                  >
                    <td style={{...styles.td, color: chatDoc.status ? '#f1fa8c' : '#6272a4', fontSize: '13px', fontWeight: chatDoc.status ? 'bold' : 'normal'}}>
                      {chatDoc.status || '-'}
                    </td>
                    <td style={styles.td}>{chatDoc.customer_phone}</td>
                    <td style={styles.td}>
                      {getOrderNumberForChat(chatDoc.customer_phone)}
                    </td>
                    <td style={styles.td}>
                      {chatDoc.messages && chatDoc.messages.length > 0 
                        ? `${chatDoc.messages[chatDoc.messages.length - 1].role === 'user' ? 'العميل: ' : 'البوت: '}${String(chatDoc.messages[chatDoc.messages.length - 1].content).substring(0, 50)}...`
                        : 'لا توجد رسائل'}
                    </td>
                    <td style={styles.td}>{formatFirestoreTimestamp(chatDoc.last_updated_timestamp)}</td>
                    <td style={styles.td}>
                      <button
                        onClick={(e) => handleToggleNonAiChat(chatDoc.customer_phone, 'easyOrder', e)}
                        disabled={togglingChat[chatDoc.customer_phone]}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '5px',
                          border: 'none',
                          cursor: togglingChat[chatDoc.customer_phone] ? 'wait' : 'pointer',
                          fontWeight: 'bold',
                          fontSize: '12px',
                          transition: 'all 0.2s',
                          backgroundColor: isNonAiChat(chatDoc.customer_phone) ? '#ff5555' : '#50fa7b',
                          color: '#282a36'
                        }}
                      >
                        {togglingChat[chatDoc.customer_phone] 
                          ? '...' 
                          : isNonAiChat(chatDoc.customer_phone) 
                            ? '🤖 AI' 
                            : '👤 Manual'
                        }
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" style={{ ...styles.td, textAlign: 'center', color: '#6272a4' }}>
                    {searchChatPhone ? 'لا توجد محادثات تطابق البحث.' : 'لا يوجد محادثات لعرضها.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* جدول قاعدة المعرفة */}
      <div style={styles.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h2 style={{ color: '#f8f8f2', margin: 0 }}>
            قاعدة المعرفة (الردود الآلية)
            <span style={{...styles.questionCountBadge, backgroundColor: '#bd93f9', color: '#282a36'}}>{knowledgeBase.length}</span>
          </h2>
        </div>
        <div style={{ maxHeight: '450px', overflowY: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>تاريخ الإضافة</th>
                <th style={styles.th}>السياق (المنتج)</th>
                <th style={{...styles.th, width: '30%'}}>السؤال المحفوظ</th>
                <th style={{...styles.th, width: '30%'}}>الإجابة الآلية</th>
                <th style={styles.th}>الإجراء</th>
              </tr>
            </thead>
            <tbody>
              {loadingKnowledgeBase ? (
                <tr><td colSpan="5" style={{ ...styles.td, textAlign: 'center', color: '#6272a4' }}>جاري تحميل قاعدة المعرفة...</td></tr>
              ) : knowledgeBase.length > 0 ? (
                knowledgeBase.map((item, index) => ( 
                  <tr key={item.id} style={index % 2 === 0 ? styles.trEven : styles.trOdd}>
                    <td style={styles.td}>{formatFirestoreTimestamp(item.date_added)}</td>
                    <td style={{...styles.td, color: '#f1fa8c', fontSize: '13px'}}>{item.product_context || 'سؤال عام'}</td>
                    <td style={styles.td}>{item.question_text}</td>
                    <td style={{...styles.td, color: '#50fa7b'}}>{item.answer_text}</td>
                    <td style={{...styles.td, textAlign: 'center'}}>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <button onClick={() => handleOpenEditModal(item)} style={styles.editButton}>تعديل ✏️</button>
                        <button onClick={() => handleDeleteKnowledgeItem(item.id)} style={styles.deleteButton}>مسح 🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" style={{ ...styles.td, textAlign: 'center', color: '#6272a4' }}>
                    قاعدة المعرفة فارغة حالياً.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* جدول الأسئلة */}
      <div style={styles.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h2 style={{ color: '#f8f8f2', margin: 0 }}>
            أسئلة العملاء (تحتاج رد)
            <span style={styles.questionCountBadge}>{questions.length}</span>
          </h2>
        </div>
        <div style={{ maxHeight: '450px', overflowY: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>تاريخ السؤال</th>
                <th style={styles.th}>رقم العميل</th>
                <th style={styles.th}>رقم الأوردر</th>
                <th style={styles.th}>المنتج (للسياق)</th>
                <th style={{...styles.th, width: '35%'}}>نص السؤال</th> 
                <th style={styles.th}>الإجراء</th>
              </tr>
            </thead>
            <tbody>
              {loadingQuestions ? (
                <tr><td colSpan="6" style={{ ...styles.td, textAlign: 'center', color: '#6272a4' }}>جاري تحميل الأسئلة...</td></tr>
              ) : questions.length > 0 ? (
                questions.map((q, index) => ( 
                  <tr 
                    key={q.id} 
                    style={{
                      ...(index % 2 === 0 ? styles.trEven : styles.trOdd),
                      ...chatStyles.clickableRow
                    }}
                    onClick={() => handleOpenChatModal({ customer_phone: q.customer_phone, customer_name: q.customer_name || '', id: q.id })}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(139, 233, 253, 0.08)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'transparent' : 'rgba(255, 255, 255, 0.02)';
                    }}
                  >
                    <td style={styles.td}>{formatFirestoreTimestamp(q.date_added)}</td>
                    <td style={styles.td}>{q.customer_phone}</td>
                    <td style={styles.td}>{getOrderNumberForChat(q.customer_phone)}</td>
                    <td style={{...styles.td, color: '#f1fa8c', fontSize: '13px'}}>{q.product_context || 'N/A'}</td>
                    <td style={styles.td}>{q.whatsapp_message}</td>
                    <td style={{...styles.td, textAlign: 'center'}}>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <button onClick={(e) => { e.stopPropagation(); handleOpenReplyModal(q); }} style={styles.replyButton}>رد 💬</button>
                        <button onClick={(e) => { e.stopPropagation(); handleDeleteQuestion(q.id); }} style={styles.deleteButton}>مسح 🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" style={{ ...styles.td, textAlign: 'center', color: '#6272a4' }}>
                    لا توجد أسئلة جديدة تحتاج لرد. 10/10
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* المودالز */}
      {showQrModal && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modalContent}>
            <h3 style={{color: '#f1fa8c'}}>امسح الكود لربط واتساب</h3>
            {qrString ? (
              <div style={{ background: 'white', padding: '15px', display: 'inline-block', borderRadius: '5px', marginTop: '10px' }}>
                {/* 🔥 لو الـ QR جاي من Baileys (base64 image) */}
                {qrString.startsWith('data:image') ? (
                  <img 
                    src={qrString} 
                    alt="WhatsApp QR Code" 
                    style={{ width: '256px', height: '256px', display: 'block' }}
                  />
                ) : (
                  /* لو جاي من WPPConnect (text format) */
                  <QRCodeCanvas value={qrString} size={256} />
                )}
              </div>
            ) : ( 
              <div style={{textAlign: 'center', padding: '40px 20px'}}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  border: '4px solid #44475a',
                  borderTop: '4px solid #8be9fd',
                  borderRadius: '50%',
                  margin: '0 auto 20px',
                  animation: 'spin 1s linear infinite'
                }}></div>
                <p style={{color: '#8be9fd', fontSize: '16px', margin: 0}}>
                  {isDeletingSession ? 'جاري حذف السيشن القديم...' : 'جاري توليد الكود...'}
                </p>
                <p style={{color: '#6272a4', fontSize: '14px', marginTop: '10px'}}>
                  {isDeletingSession ? 'الرجاء الانتظار، قد يستغرق الأمر بضع ثوان' : 'قد يستغرق الأمر بضع ثوان'}
                </p>
                <style>{`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}</style>
              </div>
            )}
            <button onClick={() => setShowQrModal(false)} style={styles.closeModalButton}>إغلاق</button>
          </div>
        </div>
      )}

      {selectedQuestion && (
        <div style={styles.modalBackdrop}>
          <div style={{...styles.modalContent, ...styles.replyModalContent}}>
            <h3 style={{color: '#8be9fd', marginTop: 0}}>الرد على سؤال العميل</h3>
            <div style={styles.questionBox}>
              <strong>سؤال العميل:</strong>
              <p style={{marginTop: '5px', color: '#f8f8f2'}}>{selectedQuestion.whatsapp_message}</p>
              {selectedQuestion.product_context && (
                <div style={{borderTop: '1px solid #44475a', paddingTop: '10px', marginTop: '10px'}}>
                  <strong style={{color: '#bd93f9'}}>بخصوص منتج:</strong>
                  <p style={{marginTop: '5px', color: '#f1fa8c', background: 'rgba(0,0,0,0.2)', padding: '5px 8px', borderRadius: '4px', fontSize: '15px'}}>
                    {selectedQuestion.product_context}
                  </p>
                </div>
              )}
            </div>
            <textarea
              style={styles.replyTextarea}
              placeholder="اكتب ردك هنا..."
              rows="5"
              value={answerText}
              onChange={(e) => setAnswerText(e.target.value)}
              disabled={isSubmitting}
              onKeyDown={(e) => handleTextareaEnterPress(e, 'answer')}
            />
            <div style={styles.checkboxContainer}>
              <input 
                type="checkbox" 
                id="saveToKB" 
                style={styles.checkboxInput}
                checked={saveToKnowledgeBase}
                onChange={(e) => setSaveToKnowledgeBase(e.target.checked)}
                disabled={isSubmitting}
              />
              <label htmlFor="saveToKB" style={styles.checkboxLabel}>
                حفظ الإجابة دي في قاعدة المعرفة (للردود الآلية مستقبلاً)
              </label>
            </div>
            <div style={styles.modalActions}>
              <button 
                onClick={handleCloseReplyModal} 
                style={{...styles.closeModalButton, marginTop: 0, background: '#6272a4'}}
                disabled={isSubmitting}
              >
                إلغاء
              </button>
              <button 
                onClick={handleSubmitAnswer} 
                style={{...styles.replyButton, padding: '10px 20px', fontSize: '16px', marginTop: 0}}
                disabled={isSubmitting || !answerText.trim()}
              >
                {isSubmitting ? 'جاري الإرسال...' : 'إرسال الإجابة 🚀'}
              </button>
            </div>
           </div>
          </div>
      )}
      
      {editingItem && (
        <div style={styles.modalBackdrop}>
          <div style={{...styles.modalContent, ...styles.replyModalContent}}>
            <h3 style={{color: '#f1fa8c', marginTop: 0}}>تعديل الإجابة الآلية</h3>
            <div style={styles.questionBox}>
              <strong>السؤال المحفوظ:</strong>
              <p style={{marginTop: '5px', color: '#f8f8f2'}}>{editingItem.question_text}</p>
              {editingItem.product_context && (
                <div style={{borderTop: '1px solid #44475a', paddingTop: '10px', marginTop: '10px'}}>
                  <strong style={{color: '#bd93f9'}}>بخصوص منتج:</strong>
                  <p style={{marginTop: '5px', color: '#f1fa8c', background: 'rgba(0,0,0,0.2)', padding: '5px 8px', borderRadius: '4px', fontSize: '15px'}}>
                    {editingItem.product_context}
                  </p>
                </div>
              )}
            </div>
            <label htmlFor="editAnswer" style={{display: 'block', marginBottom: '10px', fontWeight: 'bold'}}>الإجابة الآلية:</label>
            <textarea
              id="editAnswer"
              style={styles.replyTextarea}
              placeholder="اكتب الإجابة المعدلة هنا..."
              rows="5"
              value={editedAnswer}
              onChange={(e) => setEditedAnswer(e.target.value)}
              disabled={isSubmittingEdit}
              onKeyDown={(e) => handleTextareaEnterPress(e, 'edit')}
            />
            <div style={styles.modalActions}>
              <button 
                onClick={handleCloseEditModal} 
                style={{...styles.closeModalButton, marginTop: 0, background: '#6272a4'}}
                disabled={isSubmittingEdit}
              >
                إلغاء
              </button>
              <button 
                onClick={handleSubmitEdit} 
                style={{...styles.editButton, padding: '10px 20px', fontSize: '16px', marginTop: 0}}
                disabled={isSubmittingEdit || !editedAnswer.trim()}
              >
                {isSubmittingEdit ? 'جاري الحفظ...' : 'حفظ التعديل ✅'}
              </button>
            </div>
           </div>
          </div>
      )}

      {/* مودال الشات */}
      <ChatHistoryModal
        order={selectedOrderForChat}
        history={chatHistory}
        loading={loadingChat}
        onClose={handleCloseChatModal}
        isManualMode={selectedOrderForChat && isNonAiChat(selectedOrderForChat.customer_phone)}
        messageInput={messageInput}
        setMessageInput={setMessageInput}
        onSendMessage={handleSendMessage}
        isSending={isSendingMessage}
      />

      {/* 🔥 Modal بيانات العميل */}
      {showClientInfoModal && (
        <div style={styles.modalBackdrop} onClick={() => setShowClientInfoModal(false)}>
          <div style={{...styles.modalContent, maxWidth: '500px'}} onClick={(e) => e.stopPropagation()}>
            <h2 style={{color: '#bd93f9', marginTop: 0}}>📋 بيانات العميل</h2>
            
            <div style={{marginBottom: '20px'}}>
              <div style={{marginBottom: '15px', padding: '10px', background: 'rgba(189, 147, 249, 0.1)', borderRadius: '5px'}}>
                <strong style={{color: '#bd93f9'}}>اسم البراند:</strong>
                <div style={{fontSize: '18px', marginTop: '5px'}}>{clientInfo.brand_name}</div>
              </div>
              
              <div style={{marginBottom: '15px', padding: '10px', background: 'rgba(189, 147, 249, 0.1)', borderRadius: '5px'}}>
                <strong style={{color: '#bd93f9'}}>معرف العميل:</strong>
                <div style={{fontSize: '18px', marginTop: '5px'}}>{clientInfo.client_id}</div>
              </div>
              
              <div style={{marginBottom: '15px', padding: '10px', background: 'rgba(189, 147, 249, 0.1)', borderRadius: '5px'}}>
                <strong style={{color: '#bd93f9'}}>الإيميل:</strong>
                <div style={{fontSize: '16px', marginTop: '5px'}}>{clientInfo.email}</div>
              </div>
              
              <div style={{marginBottom: '15px', padding: '10px', background: 'rgba(189, 147, 249, 0.1)', borderRadius: '5px'}}>
                <strong style={{color: '#bd93f9'}}>نوع المنصة:</strong>
                <div style={{fontSize: '16px', marginTop: '5px'}}>{clientInfo.platform_type === 'shopify' ? '🛍️ Shopify' : '📦 EasyOrder'}</div>
              </div>
              
              <div style={{marginBottom: '15px', padding: '10px', background: 'rgba(189, 147, 249, 0.1)', borderRadius: '5px'}}>
                <strong style={{color: '#bd93f9'}}>دومين المتجر:</strong>
                <div style={{fontSize: '14px', marginTop: '5px'}}>{clientInfo.shopify_domain || clientInfo.easyOrder_domain}</div>
              </div>
              
              <div style={{marginBottom: '15px', padding: '10px', background: 'rgba(80, 250, 123, 0.1)', borderRadius: '5px'}}>
                <strong style={{color: '#50fa7b'}}>رقم واتساب صاحب البراند:</strong>
                <input
                  type="text"
                  value={editingWhatsapp}
                  onChange={(e) => setEditingWhatsapp(e.target.value)}
                  placeholder="مثال: 01012345678 أو 201012345678"
                  style={{
                    width: '100%',
                    padding: '10px',
                    marginTop: '10px',
                    fontSize: '16px',
                    background: '#282a36',
                    color: '#f8f8f2',
                    border: '1px solid #50fa7b',
                    borderRadius: '5px',
                    boxSizing: 'border-box'
                  }}
                  disabled={isSavingWhatsapp}
                />
                <div style={{fontSize: '12px', color: '#6272a4', marginTop: '5px'}}>
                  💡 سيتم إضافة 20 تلقائياً للأرقام المصرية
                </div>
              </div>
            </div>
            
            <div style={{display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
              <button
                onClick={() => setShowClientInfoModal(false)}
                style={{
                  ...styles.closeModalButton,
                  background: '#6272a4',
                  marginTop: 0
                }}
                disabled={isSavingWhatsapp}
              >
                إغلاق
              </button>
              <button
                onClick={handleSaveWhatsapp}
                style={{
                  padding: '10px 20px',
                  background: '#50fa7b',
                  color: '#282a36',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: isSavingWhatsapp ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold',
                  opacity: isSavingWhatsapp ? 0.6 : 1
                }}
                disabled={isSavingWhatsapp}
              >
                {isSavingWhatsapp ? '⏳ جاري الحفظ...' : '💾 حفظ التعديلات'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// 🔥 ==========================================================
// 🔥 الكومبوننت الأساسية
// 🔥 ==========================================================
function ClientDashboard({ user, viewingClientUid, isSuperAdminView }) {
  const [clientInfo, setClientInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleLogout = useCallback(() => {
    signOut(auth).then(() => {
      navigate('/');
    }).catch((error) => {
      console.error('Logout Error:', error);
    });
  }, [navigate]);

  useEffect(() => {
    // لو Super Admin View، نستخدم viewingClientUid
    // لو عميل عادي، نستخدم user.uid
    let targetUid;
    
    if (isSuperAdminView && viewingClientUid) {
      console.log('🔐 Super Admin viewing client with UID:', viewingClientUid);
      targetUid = viewingClientUid;
    } else if (user?.uid) {
      targetUid = user.uid;
    } else {
      console.error("No UID available for client query!"); 
      setLoading(false); 
      return;
    }
    
    // 🔥 أولاً: نحاول نلاقي العميل مع platform_type: 'easyorder'
    let clientQuery = query(
      collection(db, 'clients_v2'), 
      where('auth_uid', '==', targetUid),
      where('platform_type', '==', 'easyorder')
    );
    
    const unsubscribe = onSnapshot(clientQuery, (snapshot) => {
      if (snapshot.empty) { 
          console.error('❌ [EasyOrder Dashboard] Client not found with platform_type=easyorder'); 
          console.log('🔍 [EasyOrder Dashboard] Checking if client exists without platform filter...');
          
          // 🔥 Fallback: نبحث بدون platform_type للتشخيص
          const fallbackQuery = query(
            collection(db, 'clients_v2'), 
            where('auth_uid', '==', targetUid)
          );
          
          getDocs(fallbackQuery).then((fallbackSnapshot) => {
            if (fallbackSnapshot.empty) {
              console.error('❌ No client found at all for this auth_uid');
              setError('بيانات العميل غير موجودة في النظام');
              if (!viewingClientUid) handleLogout();
            } else {
              const clientData = fallbackSnapshot.docs[0].data();
              const actualPlatform = clientData.platform_type || 'غير محدد';
              console.error(`❌ Client found but has platform_type: "${actualPlatform}"`);
              setError(`هذا الحساب مسجل كـ ${actualPlatform === 'shopify' ? 'Shopify' : actualPlatform}. يرجى استخدام Dashboard المناسب.`);
              if (!viewingClientUid) {
                // لو عميل عادي، نوجهه للـ dashboard الصح
                setTimeout(() => handleLogout(), 3000);
              }
            }
          });
      } else { 
          console.log('✅ [EasyOrder Dashboard] Client loaded successfully');
          setClientInfo(snapshot.docs[0].data()); 
      }
      setLoading(false); 
    }, (error) => { 
      console.error("Error fetching client data:", error); 
      setError('فشل تحميل بيانات العميل');
      setLoading(false); 
    });
    
    return () => unsubscribe();
  }, [user, viewingClientUid, isSuperAdminView, handleLogout]); 

  
  if (loading) { 
    return <div style={loadingScreenStyles.container}>جاري تحميل بيانات البراند...</div>; 
  }
  
  if (error) { 
    return (
      <div style={loadingScreenStyles.error}>
        {error}
        {viewingClientUid ? (
          <p style={{marginTop: '20px', fontSize: '16px'}}>
            لم يتم العثور على بيانات العميل
          </p>
        ) : (
          <button onClick={handleLogout} style={{...styles.logoutButton, marginTop: '20px'}}>تسجيل الخروج</button>
        )}
      </div>
    ); 
  }

  if (!clientInfo) { 
    return <div style={loadingScreenStyles.container}>لا يوجد بيانات...</div>;
  }

  if (!clientInfo.ezy_webhook_secret) {
    return (
      <WebhookSetupPage 
        user={user} 
        clientData={clientInfo} 
        setClientData={setClientInfo} 
      />
    );
  }

  return <RealDashboard user={user} clientInfo={clientInfo} handleLogout={handleLogout} />;
}


// === الستايلات ===
const styles = {
    dashboardContainer: { padding: '25px', backgroundColor: '#1e1e2f', color: '#e0e0e0', minHeight: '100vh', },
    logoutButton: { background: '#ff5555', color: 'white', border: 'none', padding: '10px 15px', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold', transition: 'background-color 0.3s ease', },
    topInfoContainer: { display: 'flex', alignItems: 'stretch', gap: '20px', margin: '25px 0', },
    infoBox: { 
      background: '#282a36', 
      padding: '25px', 
      borderRadius: '10px', 
      border: '1px solid #44475a', 
      textAlign: 'center', 
      display: 'flex', 
      flexDirection: 'column', 
      justifyContent: 'space-between', 
      flexGrow: 1, 
      boxShadow: '0 4px 12px rgba(0,0,0,0.5)', 
      transition: 'transform 0.2s ease-out', 
    },
    savedBox: { background: 'rgba(255, 85, 85, 0.15)', borderColor: '#ff5555', color: '#ffb8b8', },
    waStatusInline: { display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '15px 0', fontSize: '18px', },
    statusLight: { width: '18px', height: '18px', borderRadius: '50%', marginRight: '10px', border: '1px solid rgba(255,255,255,0.2)', flexShrink: 0, },
    reconnectButton: { background: '#8be9fd', color: '#282a36', border: 'none', padding: '10px 15px', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold', fontSize: '15px', marginTop: '15px', transition: 'all 0.3s ease', },
    deleteWhatsAppButton: { background: '#ff5555', color: '#282a36', border: 'none', padding: '10px 15px', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold', fontSize: '15px', transition: 'all 0.3s ease', },
    card: { background: '#282a36', padding: '25px', margin: '25px 0', borderRadius: '10px', border: '1px solid #44475a', boxShadow: '0 4px 12px rgba(0,0,0,0.5)', },
    cardContainer: { display: 'flex', flexWrap: 'wrap', gap: '20px', margin: '25px 0', },
    statCard: { 
      flexGrow: 1, 
      flexBasis: '220px', 
      background: '#282a36', 
      border: '1px solid #44475a', 
      padding: '20px', 
      borderRadius: '10px', 
      textAlign: 'center', 
      boxShadow: '0 2px 8px rgba(0,0,0,0.4)', 
      cursor: 'pointer', 
      transition: 'all 0.2s ease-in-out', 
      color: '#bd93f9', 
    },
    statNumber: { fontSize: '2.5em', fontWeight: 'bold', margin: '10px 0 0 0', color: '#f8f8f2', },
    statPercentage: { fontSize: '1.2em', fontWeight: '600', margin: '5px 0 0 0', color: '#8be9fd', opacity: 0.9, },
    activeCardHighlight: { border: '2px solid #8be9fd', boxShadow: '0 4px 15px rgba(139, 233, 253, 0.3)', transform: 'translateY(-5px)', color: '#8be9fd', }, 
    downloadButton: { background: '#50fa7b', color: '#282a36', padding: '8px 15px', textDecoration: 'none', borderRadius: '5px', fontWeight: 'bold', fontSize: '14px', transition: 'all 0.3s ease', border: 'none', },
    table: { width: '100%', borderCollapse: 'separate', borderSpacing: 0, marginTop: '20px', borderRadius: '8px', overflow: 'hidden', },
    th: { borderBottom: '2px solid #6272a4', padding: '12px 15px', background: '#44475a', textAlign: 'right', fontSize: '14px', color: '#f8f8f2', fontWeight: 'bold', position: 'sticky', top: 0, zIndex: 1, },
    td: { borderBottom: '1px solid #44475a', padding: '12px 15px', textAlign: 'right', fontSize: '14px', color: '#e0e0e0', wordBreak: 'break-word', verticalAlign: 'top', },
    trOdd: { backgroundColor: 'transparent' }, 
    trEven: { backgroundColor: 'rgba(255, 255, 255, 0.02)' }, 
    filterContainer: { display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', marginBottom: '25px', padding: '15px', border: '1px solid #44475a', borderRadius: '8px', backgroundColor: 'rgba(0, 0, 0, 0.2)', },
    filterButton: { padding: '8px 15px', border: '1px solid #bd93f9', background: 'transparent', color: '#bd93f9', cursor: 'pointer', borderRadius: '4px', transition: 'all 0.3s ease', },
    activeFilterButton: { background: '#bd93f9', color: '#282a36' }, 
    filterInput: { padding: '8px 10px', border: '1px solid #6272a4', borderRadius: '4px', background: '#282a36', color: '#f8f8f2', outline: 'none', minWidth: '130px', },
    modalBackdrop: { position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, backdropFilter: 'blur(5px)', },
    modalContent: { background: '#282a36', padding: '40px', borderRadius: '10px', textAlign: 'center', boxShadow: '0 8px 25px rgba(0,0,0,0.7)', color: '#f8f8f2', border: '1px solid #44475a', },
    closeModalButton: { background: '#ff5555', color: '#f8f8f2', border: 'none', padding: '10px 20px', borderRadius: '5px', cursor: 'pointer', marginTop: '25px', fontSize: '16px', fontWeight: 'bold', transition: 'background-color 0.3s ease', },
    questionCountBadge: {
      backgroundColor: '#f1fa8c',
      color: '#282a36',
      padding: '3px 10px',
      borderRadius: '12px',
      fontSize: '0.8em',
      fontWeight: 'bold',
      marginLeft: '15px'
    },
    replyButton: {
      background: '#8be9fd',
      color: '#282a36',
      border: 'none',
      padding: '8px 12px',
      borderRadius: '5px',
      cursor: 'pointer',
      fontWeight: 'bold',
      fontSize: '14px',
      transition: 'all 0.3s ease',
    },
    deleteButton: {
      background: '#ff5555', 
      color: '#f8f8f2', 
      border: 'none',
      padding: '8px 12px',
      borderRadius: '5px',
      cursor: 'pointer',
      fontWeight: 'bold',
      fontSize: '14px',
      transition: 'all 0.3s ease',
    },
    editButton: {
      background: '#f1fa8c', 
      color: '#282a36', 
      border: 'none',
      padding: '8px 12px',
      borderRadius: '5px',
      cursor: 'pointer',
      fontWeight: 'bold',
      fontSize: '14px',
      transition: 'all 0.3s ease',
    },
    replyModalContent: {
      textAlign: 'right',
      width: '90%',
      maxWidth: '600px',
    },
    questionBox: {
      background: 'rgba(0,0,0,0.2)',
      padding: '15px',
      borderRadius: '8px',
      border: '1px solid #44475a',
      marginBottom: '20px',
      textAlign: 'right',
    },
    replyTextarea: {
      width: '100%',
      padding: '10px',
      borderRadius: '5px',
      border: '1px solid #6272a4',
      background: '#1e1e2f',
      color: '#f8f8f2',
      fontSize: '16px',
      fontFamily: 'inherit',
      boxSizing: 'border-box', 
      resize: 'vertical',
    },
    checkboxContainer: {
      display: 'flex',
      alignItems: 'center',
      marginTop: '20px',
      textAlign: 'right',
    },
    checkboxInput: {
      width: '18px',
      height: '18px',
      marginLeft: '10px', 
      cursor: 'pointer',
    },
    checkboxLabel: {
      cursor: 'pointer',
      fontSize: '14px',
      color: '#e0e0e0',
    },
    modalActions: {
      display: 'flex',
      justifyContent: 'space-between',
      marginTop: '30px',
    },
    autoCancelToggleContainer: {
      marginTop: '15px',
      paddingTop: '15px',
      borderTop: '1px solid #44475a',
    },
    autoCancelToggle: {
      width: '100%',
      padding: '10px 15px',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontWeight: 'bold',
      fontSize: '13px',
      color: '#282a36',
      transition: 'all 0.3s ease',
      boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
    }
};

const loadingScreenStyles = {
    container: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', fontSize: '24px', color: '#8be9fd', backgroundColor: '#1e1e2f', },
    error: { display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', fontSize: '24px', color: '#ff5555', textAlign: 'center', padding: '20px', backgroundColor: '#1e1e2f', }
};

export default ClientDashboard;